import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEntityManagement, EntityCrudService } from '../../../shared/hooks/useEntityManagement';
import { useCatalogService, ServiceComponentSummary } from '../services/catalogService';

const detailKey = (id: string | undefined, service: string | undefined) =>
  ['service-administration', 'service-component', id ?? null, service ?? null] as const;

export interface ServiceComponentFormData {
  name: string;
  description: string;
  scope: string;
  portfolio: string;
  auto_deploy: boolean;
  source_repository: string;
  depends_on: string[];
}

const EMPTY_FORM: ServiceComponentFormData = {
  name: '',
  description: '',
  scope: 'global',
  portfolio: '',
  auto_deploy: true,
  source_repository: '',
  depends_on: [],
};

/**
 * All service components across every service, for the unified Components surface
 * (one list grouped by service → detail). Components are pipeline-registered, so
 * there is no UI create path; update/delete/version operate on the selected
 * component, which always carries its own `service`.
 */
export function useServiceComponents(isOpen: boolean) {
  const catalogService = useCatalogService();
  const queryClient = useQueryClient();
  const productsRef = useRef<ServiceComponentSummary[]>([]);
  const [originalName, setOriginalName] = useState<string | null>(null);

  const crudService = useMemo((): EntityCrudService<ServiceComponentSummary, Record<string, unknown>, Record<string, unknown>> => ({
    getAll: async () => {
      const result = await catalogService.listServiceComponents();
      return result.products;
    },
    create: (dto) => catalogService.createServiceComponent(dto as Parameters<typeof catalogService.createServiceComponent>[0]),
    update: (id, dto) => {
      const product = productsRef.current.find((p) => p.id === id);
      return catalogService.updateServiceComponent(id, product?.service || '', dto);
    },
    delete: async (id) => {
      const product = productsRef.current.find((p) => p.id === id);
      await catalogService.deleteServiceComponent(id, product?.service || '');
    },
  }), [catalogService]);

  const entityManagement = useEntityManagement<ServiceComponentSummary, ServiceComponentFormData>({
    service: crudService,
    queryKey: ['service-administration', 'service-components'],
    isOpen,
    emptyForm: EMPTY_FORM,
    getEntityId: (p) => p.id,
    entityToForm: (p) => ({
      name: p.name,
      description: p.description,
      scope: p.scope,
      portfolio: p.portfolio,
      auto_deploy: p.auto_deploy,
      source_repository: p.source_repository || '',
      depends_on: p.depends_on || [],
    }),
    formToCreateDto: (form) => ({
      name: form.name,
      description: form.description || undefined,
      scope: form.scope || undefined,
      portfolio: form.portfolio || undefined,
      auto_deploy: form.auto_deploy,
      source_repository: form.source_repository || undefined,
      depends_on: form.depends_on.length > 0 ? form.depends_on : undefined,
    }),
    formToUpdateDto: (form) => ({
      name: form.name,
      description: form.description,
      scope: form.scope || undefined,
      portfolio: form.portfolio || undefined,
      auto_deploy: form.auto_deploy,
      source_repository: form.source_repository || undefined,
      depends_on: form.depends_on,
    }),
    validateForm: (form) => ({
      name: !form.name || form.name.trim().length < 2 ? 'Name must be at least 2 characters' : null,
      portfolio: !form.portfolio?.trim() ? 'Portfolio is required' : null,
    }),
    hasFormChanges: (form, product, isNew) => {
      if (isNew) return true;
      if (!product) return false;
      return (
        form.name !== product.name ||
        form.description !== product.description ||
        form.scope !== product.scope ||
        form.portfolio !== product.portfolio ||
        form.auto_deploy !== product.auto_deploy ||
        form.source_repository !== (product.source_repository || '') ||
        JSON.stringify(form.depends_on) !== JSON.stringify(product.depends_on || [])
      );
    },
  });

  const { handleSelect: baseHandleSelect, entities, isNew, successMessage, setSuccessMessage, selectedEntity } = entityManagement;

  // Keep the id→service lookup (used by update/delete) in sync with the cached
  // list, since getAll may be served from cache without re-running.
  useEffect(() => {
    productsRef.current = entities;
  }, [entities]);

  const handleSelect = useCallback((product: ServiceComponentSummary) => {
    setOriginalName(product.name);
    baseHandleSelect(product);
  }, [baseHandleSelect]);

  useEffect(() => {
    if (isNew) setOriginalName(null);
  }, [isNew]);

  const selectedEntityId = selectedEntity?.id;
  const selectedEntityService = selectedEntity?.service;

  // The selected component's detail (versions) is server state: read it through the
  // cache, keyed by the selection. Disabled with no selection — including while
  // adding a new component, which has no detail to read.
  const detailQuery = useQuery({
    queryKey: detailKey(selectedEntityId, selectedEntityService),
    queryFn: () => {
      if (!selectedEntityId || !selectedEntityService) {
        throw new Error('Cannot load a service component detail without a selection');
      }
      return catalogService.getServiceComponent(selectedEntityId, selectedEntityService);
    },
    enabled: !!selectedEntityId && !!selectedEntityService,
  });
  const productDetail = detailQuery.data ?? null;

  useEffect(() => {
    if (detailQuery.error) console.error('Failed to load service component detail:', detailQuery.error);
  }, [detailQuery.error]);

  const refreshServiceComponentDetail = useCallback(
    () => { queryClient.invalidateQueries({ queryKey: detailKey(selectedEntityId, selectedEntityService) }); },
    [queryClient, selectedEntityId, selectedEntityService],
  );

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, setSuccessMessage]);

  // Dependency options + rename-warning are scoped to the selected component's
  // own service — components only depend on siblings within the same service.
  const siblings = selectedEntityService
    ? entities.filter((p) => p.service === selectedEntityService)
    : [];

  const showRenameWarning =
    !isNew &&
    originalName !== null &&
    entityManagement.formData.name !== originalName &&
    siblings.some((p) => p.depends_on?.includes(originalName));

  const dependsOnOptions = siblings
    .filter((p) => (selectedEntity ? p.id !== selectedEntity.id : true))
    .map((p) => p.name);

  return {
    entityManagement,
    handleSelect,
    originalName,
    showRenameWarning,
    dependsOnOptions,
    productDetail,
    refreshServiceComponentDetail,
  };
}
